'use client'

import { Accessibility, BedDouble, Info, Sofa, Users } from 'lucide-react'
import React from 'react'

import { RoomDetailIconList, RoomDetailIconListItem } from '@/components/Room/RoomDetailIconList'
import type { ApartmentAvailabilityItem } from '@/lib/apartments/apartmentsQuery'
import { resolveApartmentListingFilters } from '@/lib/apartments/apartmentListingFilterOptions'
import { formatRoomCountLabel } from '@/lib/apartments/roomCountLabels'
import { useMessages } from '@/lib/i18n/useMessages'

const DETAIL_ICON_CLASS = 'size-5 shrink-0 text-[#0B765A]'

function AvailableWithIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      className={className}
      fill="none"
      height={20}
      viewBox="0 0 16 16"
      width={20}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 2V13.3333C8 13.5101 7.92976 13.6797 7.80474 13.8047C7.67971 13.9298 7.51014 14 7.33333 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H12.6667C13.0203 2 13.3594 2.14048 13.6095 2.39052C13.8595 2.64057 14 2.97971 14 3.33333V7.33333C14 7.51014 13.9298 7.67971 13.8047 7.80474C13.6797 7.92976 13.5101 8 13.3333 8H2"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M10.668 12.6666L12.0013 14L14.668 11.3333"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

type SummaryItem = {
  icon: React.ReactNode
  key: string
  label: string
}

type RoomListingSummaryProps = {
  apartment: Pick<
    ApartmentAvailabilityItem,
    'listingFilters' | 'peopleCapacity' | 'roomCount'
  >
}

export function RoomListingSummary({ apartment }: RoomListingSummaryProps) {
  const messages = useMessages()
  const copy = messages.roomDetail
  const summaryCopy = copy.listingSummary
  const gridCopy = messages.roomTypes.grid
  const listingFilters = resolveApartmentListingFilters(apartment.listingFilters)

  const items: SummaryItem[] = []

  if (apartment.roomCount > 0) {
    items.push({
      key: 'roomCount',
      icon: <BedDouble aria-hidden className={DETAIL_ICON_CLASS} />,
      label: formatRoomCountLabel(apartment.roomCount, copy),
    })
  }

  items.push({
    key: 'capacity',
    icon: <Users aria-hidden className={DETAIL_ICON_CLASS} />,
    label: (apartment.peopleCapacity === 1 ? copy.capacityLabelOne : copy.capacityLabel).replace(
      '{count}',
      String(apartment.peopleCapacity),
    ),
  })

  if (listingFilters.furnishing === 'fullyFurnishedEquipped') {
    items.push({
      key: 'furnishing',
      icon: <Sofa aria-hidden className={DETAIL_ICON_CLASS} />,
      label: gridCopy.furnishedLabelFullyEquipped,
    })
  } else if (listingFilters.furnishing === 'fullyFurnished') {
    items.push({
      key: 'furnishing',
      icon: <Sofa aria-hidden className={DETAIL_ICON_CLASS} />,
      label: gridCopy.furnishedLabelFullyFurnished,
    })
  }

  if (listingFilters.hasBalcony && listingFilters.hasPatio) {
    items.push({
      key: 'outdoor',
      icon: <AvailableWithIcon className={DETAIL_ICON_CLASS} />,
      label: summaryCopy.balconyAndPatioLabel,
    })
  } else if (listingFilters.hasBalcony) {
    items.push({
      key: 'balcony',
      icon: <AvailableWithIcon className={DETAIL_ICON_CLASS} />,
      label: summaryCopy.balconyLabel,
    })
  } else if (listingFilters.hasPatio) {
    items.push({
      key: 'patio',
      icon: <AvailableWithIcon className={DETAIL_ICON_CLASS} />,
      label: summaryCopy.patioLabel,
    })
  }

  if (listingFilters.isAccessible) {
    items.push({
      key: 'accessibility',
      icon: <Accessibility aria-hidden className={DETAIL_ICON_CLASS} />,
      label: summaryCopy.accessibleLabel,
    })
  }

  if (!items.length) {
    return null
  }

  const showFurnishingNotice = listingFilters.furnishing != null

  return (
    <section aria-labelledby="room-listing-summary-heading" className="space-y-2">
      <h2
        className="font-outfit text-base font-medium text-dark-800"
        id="room-listing-summary-heading"
      >
        {summaryCopy.title}
      </h2>
      <RoomDetailIconList aria-label={summaryCopy.ariaLabel}>
        {items.map((item) => (
          <RoomDetailIconListItem key={item.key} icon={item.icon}>
            {item.label}
          </RoomDetailIconListItem>
        ))}
      </RoomDetailIconList>
      {showFurnishingNotice ? (
        <p className="flex items-start gap-2 font-outfit text-sm leading-5 text-dark-800/75">
          <Info aria-hidden className="mt-0.5 size-4 shrink-0 text-[#0B765A]/80" />
          {summaryCopy.furnishingNotice}
        </p>
      ) : null}
    </section>
  )
}
